<template>
  <form
    v-if="intent"
    @submit.prevent="charge"
  >
    <div class="px-4 mb-3">
      <portal-payment-method-selector
        v-model="method"
        :allowed="allowedMethods"
        :card-fee="intent.card_processing_fee_rate"
      />
    </div>

    <div v-show="!settingUp">
      <div ref="paymentContainer"></div>
      <div class="px-4">
        <div class="text-gray-400 text-xs text-center mt-1 pb-4">
          By providing your payment information you allow {{ companyName }} to charge your payment method for future
          payments.
        </div>
        <button
          type="submit"
          class="flex w-full items-center justify-center space-x-2 font-medium shadow py-3 px-2 rounded-lg"
          :disabled="disableSubmit"
          :class="[
            !loading && !disableSubmit ? 'cursor-pointer bg-primary-600 text-white hover:bg-primary-700' : '',
            loading || disableSubmit ? 'cursor-not-allowed bg-primary-600 text-white' : '',
          ]"
        >
          <span
            class="inline-flex"
            v-if="!loading"
          >
            <ion-icon name="lock-closed" />
          </span>
          <span
            class="inline-flex"
            v-else
          >
            <client-loading-indicator
              :styled="false"
              class="fill-white text-primary-700 stroke-primary-900"
            />
          </span>
          <span v-if="!loading">{{ amountBtnText }}</span>
        </button>
      </div>
    </div>
    <div
      v-if="settingUp"
      class="flex justify-center py-4"
    >
      <client-loading-indicator class="text-primary-500 fill-gray-100 w-5 h-5" />
    </div>
  </form>
</template>

<script>
import Payments from "../../repository/PortalPayments"
import CurrencyUtils from "../../../js/helpers/currencyUtils"
import ClientLoadingIndicator from "../../client_auth/components/client-loading-indicator.vue"
import PortalPaymentMethodSelector from "./PortalPaymentMethodSelector.vue"
import currencyUtils from "../../../js/helpers/currencyUtils"

export default {
  name: "PortalRainforestPaymentForm",
  components: { PortalPaymentMethodSelector, ClientLoadingIndicator },

  props: {
    invoice: { required: true, type: Object },
    paymentToken: { required: false, type: String, default: "" },
    amountBtnText: { required: true, type: String },
    amtFieldFocused: { required: false, type: Boolean, default: false },
    intentAmount: { required: true, type: Number },
    amountBelowMinPaymentAmount: { required: false, default: false, type: Boolean },
    amountAboveMaxPaymentAmount: { required: false, default: false, type: Boolean },
    paymentAmountSelection: { required: false, default: "full_amount", type: String },
  },
  emits: ["approved", "intent", "loaded", "method-changed"],

  data() {
    return {
      intent: null,
      loading: false,
      disabled: true,
      method: "CARD",
      settingUp: true,
      rfHandlers: null,
      paymentEl: null,
      cancelTokenSource: null,
      requestId: 0,
    }
  },

  computed: {
    allowedMethods() {
      return this.invoice.payment_methods
        .map((item) => (item.toLowerCase() === "cc" ? "CARD" : "ACH"))
        .sort()
        .reverse()
    },

    amount() {
      return CurrencyUtils.convertAndFormatCurrency(this.intent.amount_due)
    },

    disableSubmit() {
      return (
        this.amtFieldFocused ||
        this.loading ||
        this.disabled ||
        this.amountBelowMinPaymentAmount ||
        this.amountAboveMaxPaymentAmount
      )
    },

    companyName() {
      return window?.FinancialCents?.company?.name || ""
    },
  },
  watch: {
    method() {
      this.$emit("method-changed", this.method)
      this.loadPayinConfig()
    },

    amtFieldFocused(newValue) {
      // return early if amount is below minimum payment amount or if partial payments are not allowed or its above the max payment amount
      if (
        this.amountBelowMinPaymentAmount ||
        !this.invoice.allow_partial_payments ||
        this.amountAboveMaxPaymentAmount
      ) {
        return
      }
      if (!newValue) {
        this.loadPayinConfig()
      }
    },
    paymentAmountSelection() {
      this.loadPayinConfig()
    },
  },
  beforeMount() {
    if (this.allowedMethods.includes("CARD")) {
      this.method = "CARD"
    } else {
      this.method = "ACH"
    }
    this.$emit("method-changed", this.method)
  },

  mounted() {
    this.loadPayinConfig()
  },

  beforeUnmount() {
    this.cancelPendingRequest()
    this.unbindRainforestEvents()
    this.rfHandlers = null
    this.paymentEl = null
  },

  methods: {
    cancelPendingRequest() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel("Request canceled by new request")
        this.cancelTokenSource = null
      }
    },

    unbindRainforestEvents() {
      const paymentElement = this.paymentEl
      const handler = this.rfHandlers
      if (!paymentElement || !handler) return

      paymentElement.removeEventListener("approved", handler.approved)
      paymentElement.removeEventListener("valid", handler.valid)
      paymentElement.removeEventListener("invalid", handler.invalid)
      paymentElement.removeEventListener("declined", handler.declined)
      paymentElement.removeEventListener("error", handler.error)
      paymentElement.removeEventListener("loaded", handler.loaded)

      this.rfHandlers = null
    },

    createRainforestElement() {
      const container = this.$refs.paymentContainer
      if (!container || !this.intent?.payin_config_id || !this.intent?.session_key) return false

      if (this.paymentEl) {
        this.unbindRainforestEvents()
        this.paymentEl.remove()
        this.paymentEl = null
      }

      const rfPaymentElement = document.createElement("rainforest-payment")
      rfPaymentElement.setAttribute("payin-config-id", this.intent.payin_config_id)
      rfPaymentElement.setAttribute("session-key", this.intent.session_key)
      rfPaymentElement.setAttribute("allowed-methods", this.method)
      rfPaymentElement.setAttribute("hide-card-brand-logos", "")
      rfPaymentElement.setAttribute("hide-button", "")

      this.rfHandlers = {
        approved: (e) => this.approved(e),
        valid: () => (this.disabled = false),
        invalid: () => (this.disabled = true),
        declined: () => {
          this.disabled = false
          this.loading = false
        },
        error: () => {
          this.disabled = false
          this.loading = false
        },
        loaded: () => this.$emit("loaded"),
      }

      rfPaymentElement.addEventListener("approved", this.rfHandlers.approved)
      rfPaymentElement.addEventListener("valid", this.rfHandlers.valid)
      rfPaymentElement.addEventListener("invalid", this.rfHandlers.invalid)
      rfPaymentElement.addEventListener("declined", this.rfHandlers.declined)
      rfPaymentElement.addEventListener("error", this.rfHandlers.error)
      rfPaymentElement.addEventListener("loaded", this.rfHandlers.loaded)

      container.appendChild(rfPaymentElement)
      this.paymentEl = rfPaymentElement
      return true
    },
    async loadPayinConfig() {
      // Cancel any pending request
      this.cancelPendingRequest()

      // Track this request to prevent stale callbacks
      const currentRequestId = ++this.requestId

      // Create new cancel token for this request
      const CancelToken = axios.CancelToken
      this.cancelTokenSource = CancelToken.source()

      this.settingUp = true
      this.disabled = true

      try {
        const intentAmount =
          !this.invoice.allow_partial_payments || this.paymentAmountSelection === "full_amount"
            ? null
            : currencyUtils.convertToCents(this.intentAmount)
        const { data } = await Payments.getRainforestPaymentIntent(
          this.invoice,
          this.method,
          intentAmount,
          this.cancelTokenSource.token,
        )

        // Only process if this is still the current request
        if (currentRequestId !== this.requestId) {
          return
        }

        this.intent = data.data
        this.$emit("intent", data.data)
        this.cancelTokenSource = null

        this.$nextTick(() => {
          // Double-check we're still current before processing callback
          if (currentRequestId !== this.requestId) {
            return
          }

          const created = this.createRainforestElement()
          if (!created) {
            // Element creation failed - show error and emit loaded so parent doesn't get stuck
            this.$alert("Unable to load payment form. Please refresh the page and try again.")
            this.$emit("loaded")
          }
          this.settingUp = false
        })
      } catch (e) {
        // Ignore errors from canceled requests
        if (axios.isCancel(e)) {
          return
        }

        // Only update state if still current
        if (currentRequestId !== this.requestId) {
          return
        }

        this.cancelTokenSource = null
        this.settingUp = false
        if (e.response?.status === 403) {
          this.$alert("You are not authorized to complete this action.")
        } else {
          this.$alert("Unable to complete your request. Please try refreshing the page and try again.")
        }
        // Emit loaded so parent doesn't get stuck waiting
        this.$emit("loaded")
        console.error(e)
      }
    },

    approved(data) {
      this.$emit("approved", data)
      this.loading = false
    },

    /**
     * Verify that the payment intent is still valid before submitting payment.
     *
     * @returns {Promise<void>}
     */
    async charge() {
      this.loading = true
      try {
        const paymentAmount =
          !this.invoice.allow_partial_payments || this.paymentAmountSelection === "full_amount"
            ? null
            : this.intent.amount_due
        await axios.get(route("client-portal.rainforest.can-pay", this.invoice), {
          params: {
            payment_token: this.invoice.payment_token || this.paymentToken,
            payment_method_type: this.method,
            payment_amount: paymentAmount,
          },
        })

        if (!this.paymentEl) {
          this.$alert("There was an error loading the payment form. Please refresh the page and try again.")
          this.loading = false
          return
        }

        this.paymentEl.submit()
      } catch (e) {
        console.error(e)
        this.loading = false
        if (e.response?.status === 403) {
          this.$alert("You are not authorized to complete this action.")
        } else {
          this.$alert("Unable to complete your request. Please try again.")
        }
      }
    },
  },
}
</script>
